/**
 * marker-drawer.js
 */

import React from 'react';
import {
  darken,
  lighten,
  styled,
  useTheme,
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import usePrevious from 'utils/use-previous';
import analytics from 'utils/analytics';
import Icon from 'components/icon';
import TitleEditor from 'components/title-editor';
import FeatureBox from 'components/feature-box';
import ConfirmDialog from 'components/confirm-dialog/confirm-dialog';
import { UserContext } from 'contexts/user/user-context';
import { TrackContext } from '../track-context';
import { MapContext } from '../map/map-context';
import { 
  DrawerHeader,
  DrawerControlBar,
  DrawerBase,
  GridCell,
  DistanceGridCell,
  SelectedTrackGrid,
  MarkerElapsedGridCell,
  MarkerTimeGridCell,
} from './control-common';
import {
  DrawerContextMenu,
} from './drawer-context-menu';


const PhotoButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: darken(theme.palette.common.white, 0.1),
  color: lighten(theme.palette.common.black, 0.2),
  opacity: 0.7,
  '&:hover': {
    backgroundColor: darken(theme.palette.common.white, 0.2),
    opacity: 0.8,
  },
}));


const Header = () => {
  const theme = useTheme();
  const titleColor = theme.palette.sys.onSecondary;
  const { deselectMarker } = React.useContext(MapContext);


  return (
    <DrawerHeader backgroundColor={theme.palette.sys.secondary}>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 2, }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', }}>
          <Button
            startIcon={<Icon type='nav-back' />}
            children='Back'
            sx={{ color: titleColor, textTransform: 'none' }}
            onClick={deselectMarker}
          />
        </Box>
        <Typography variant='subtitle1' sx={{ flex: 3, display: 'flex', justifyContent: 'center', color: titleColor }}>
          PIN
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', }}>
          <Icon type='marker' sx={{ mr: 1, color: titleColor }} />
        </Box>
      </Box>
    </DrawerHeader>
  )
}


export default function MarkerDrawer(props) {
  const {
    onDelete,
    ...rest
  } = props;
  const { user } = React.useContext(UserContext);
  const { tracks } = React.useContext(TrackContext);
  const { 
    selectedMarkerUuid, 
    deleteMarker, 
    updateMarker,
  } = React.useContext(MapContext);  
  const theme = useTheme();
  const formContext = useForm({ shouldUnregister: true });
  const { watch, reset }  = formContext;
  const watchSubscription = React.useRef();
  const [photoDialogOpen, setPhotoDialogOpen] = React.useState(false);
  const [confirmRemovePhoto, setConfirmRemovePhoto] = React.useState();
  const [photoLoading, setPhotoLoading] = React.useState(false);


  let marker, track;
  for (const t of tracks) {
    marker = t.markers?.find((m) => m.uuid === selectedMarkerUuid);
    if (marker) {
      track = t;
      break;
    }
  }


  const canEdit = user
    ? !user.role.isSupervisor && marker?.userId === user.id
    : (marker && !marker.userId);


  // initialize form
  React.useEffect(() => {
    reset({
      label: marker?.label || '',
    });
  }, [marker?.label, reset]);


  // persist changes
  React.useEffect(() => {
    watchSubscription.current = watch((data) => {
      // isDirty/reset should work but doesn't...maybe because of how we're
      // handling onChange in TitleEditor?
      //if (isDirty) {}
      if (data.label !== marker.label) {
        if (data.label && !marker.label) {
          analytics.trackGA4('event', {
            name: 'track_pin_add_content', params: {
              trackpin_track_type: track.type,
              trackpin_field: 'label',
              trackpin_user_role: user
                ? user.role.name.toLowerCase()
                : 'anonymous'
            }
          });
        }

        updateMarker(track, marker.uuid, data);
        //reset(data);
      }
    })
    return () => watchSubscription.current.unsubscribe();
  }, [track, marker?.label, marker?.uuid, updateMarker, watch, user])


  const { getRootProps, getInputProps, open: openDropzone } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: 'image/jpeg',
    disabled: !canEdit,
    onDropAccepted: (files) => {
      setPhotoLoading(true);
      const file = files[0];

      if (!marker.photoUrl) {
        analytics.trackGA4('event', {
          name: 'track_pin_add_content', params: {
            trackpin_track_type: track.type,
            trackpin_field: 'photo',
            trackpin_user_role: user
              ? user.role.name.toLowerCase()
              : 'anonymous'
          }
        });
      }      

      updateMarker(track, marker.uuid, {}, file);
    },
  });


  // A change in the marker's photo URL indicates that the files has finshed
  // uploading. This assumes that changes to the photo, e.g. selecting a new 
  // file or deleting the photo, will not happen while a photo is uploading, 
  // which we enforce by disabling interaction with the photo UI during upload.
  // (This more or less mirrors what we're doing with photoPreview, but if
  // we rely on the presence of photoPreview to determine loading state, there
  // will be an initial delay while the uploaded file is being processed for the preview.)
  const prevPhotoUrl = usePrevious(marker?.photoUrl);
  React.useEffect(() => {
    if (marker?.photoUrl && marker.photoUrl !== prevPhotoUrl) {
      setPhotoLoading(false);
    }
  }, [marker?.photoUrl, prevPhotoUrl]);


  if (!marker) {
    return null;
  };


  const removePhoto = async () => {
    if (await confirmRemovePhoto()) {
      updateMarker(track, marker.uuid, { photoUrl: null });
    }
  }


  const handleDeleteMarker = () => {
    watchSubscription.current.unsubscribe()
    if (onDelete) {
      onDelete();
    }

    deleteMarker(track, selectedMarkerUuid)
  };


  const controls = canEdit 
    ? <DrawerControlBar
      menu={<DrawerContextMenu
        items={[
          {
            id: 'delete-pin-marker',
            icon: 'delete',
            name: 'Delete Pin',
            onSelect: () => {
              analytics.trackGA4('event', {
                name: 'track_pin_delete', params: {
                  trackpin_track_type: track.type,
                  trackpin_user_role: user
                    ? user.role.name.toLowerCase()
                    : 'anonymous'
                }
              });

              handleDeleteMarker();
            },
          }
        ]}
      />}
    />
    : undefined;


  return (
    <>
      <FormProvider {...formContext}>
        <DrawerBase
          {...rest}
          canCollapse={false}
          header={<Header />}
          controls={controls}
          content={<>
            <Grid container spacing={2}>
              <Grid item xs={5} md={3}>
                <input {...getInputProps()} />
                <FeatureBox loading={photoLoading}>
                  {(marker.photoUrl || marker.photoPreview)
                    ? 
                      <Box
                        {...getRootProps({ className: 'dropzone' })}
                        sx={{ cursor: canEdit ? 'pointer' : undefined, position: 'relative', display: 'flex' }}
                        onClick={() => setPhotoDialogOpen(true)}
                      >
                        <Box sx={{ width: '100%', aspectRatio: 1, backgroundColor: 'purple', display: 'flex' }}>
                          <img
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            src={marker.photoPreview ? marker.photoPreview : marker.photoUrl}
                            alt=""
                          />
                        </Box>
                        {canEdit &&
                          <>
                            <Box
                              sx={{ position: 'absolute', padding: theme.spacing(0.5), bottom: 0, left: 0 }}
                              onClick={(e) => { e.stopPropagation(); removePhoto(); }}
                            >
                              <PhotoButton size='small'>
                                <Icon type='delete' />
                              </PhotoButton>
                            </Box>
                            <Box
                              sx={{ position: 'absolute', padding: theme.spacing(0.5), bottom: 0, right: 0 }}
                              onClick={(e) => { e.stopPropagation(); openDropzone(); }}
                            >
                              <PhotoButton size='small'>
                                <Icon type='edit' />
                              </PhotoButton>
                            </Box>
                          </>
                        }
                      </Box>
                    : <Box
                      {...getRootProps({ className: 'dropzone' })}
                      onClick={openDropzone}
                      sx={{
                        backgroundColor: theme.palette.sys.surfaceContainerHigh,
                        color: 'primary.main',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 3,
                        cursor: canEdit ? 'pointer' : undefined,
                      }}
                    >
                      <Icon type={canEdit ? 'add-photo' : 'photo'} sx={{ fontSize: 60 }} />
                      {canEdit &&
                        <Box sx={{ fontWeight: 'bold', marginTop: 2 }}>ADD PHOTO</Box>
                      }
                    </Box>
                  }
                </FeatureBox>
              </Grid>
              <Grid item xs={7} md={9}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <GridCell
                      sx={{ marginBottom: '-6px', marginTop: '-6px' }}
                      iconId='label'
                      content={
                        <TitleEditor
                          propertyName='label'
                          inputLabel={'Label'}
                          disabled={!canEdit}
                          rules={{
                            maxLength: {
                              value: 20,
                              message: 'Label max length is 20 chars'
                            }
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MarkerTimeGridCell track={track} marker={marker} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MarkerElapsedGridCell track={track} marker={marker} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DistanceGridCell distance={marker.distanceOnTrack} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GridCell
                      iconId='pin-creator'
                      content={marker.userName ?? 'Anonymous'}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <SelectedTrackGrid track={track} />
                </Box>

                
              </Grid>
            </Grid>


            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <SelectedTrackGrid track={track} />
            </Box>

          </>}
        />
      </FormProvider>

      {/* REMOVE PHOTO CONFIRM DIALOG */}
      <ConfirmDialog
        titleStyle='warning'
        title='Confirm Delete Photo'
        titleIcon='warning'
        setConfirm={setConfirmRemovePhoto}
        content='Are you sure you want to delete this photo permanently?'
        customActions={[
          {
            label: 'Cancel',
            value: false,
            isNotPriority: true,
          },
          {
            label: 'Delete',
            value: true,
          }
        ]}
      />

      {/* FULLSCREEN PHOTO DIALOG */}
      <Dialog 
        fullScreen
        open={photoDialogOpen && marker.photoUrl}
        onClose={() => setPhotoDialogOpen(false)}
      >
        <DialogContent>
          <Box position='relative' width='100%' height='100%'>
            <Button
              color='grey'
              onClick={() => setPhotoDialogOpen(false)}
              variant='contained'
              sx={{
                position: 'absolute', 
                right: 4, 
                top: 4, 
                padding: 1, 
                opacity: 0.8,
                minWidth: 'auto',
                borderRadius: '50%' }}
            >
              <Icon type='close' />
            </Button>
            <img
              style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
              src={marker.photoUrl}
              alt=""
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}